import type { ReactNode } from 'react'
import { AxiosError } from 'axios'
import { notification, Typography } from 'antd'

const { Text, Paragraph } = Typography

export const ToastLifeTime = {
  Short: 5,
  Long: 10,
  OneDay: 86400,
} as const

export type ToastLifeTime = (typeof ToastLifeTime)[keyof typeof ToastLifeTime]

export type ErrorType = {
  lifeTime?: number
  onClose?: () => void
  message: ReactNode | string
}
function extractBackendMessage(error: any): ReactNode {
  const data = error?.response?.data

  if (!data) return error?.message ?? 'Unknown error'

  if (typeof data === 'string') return data.split('\n')[0]

  if (typeof data.error === 'string') return data.error

  if (typeof data.message === 'string') return data.message

  if (typeof data.errors === 'object') {
    return <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(data.errors, null, 2)}</pre>
  }

  return error?.message ?? 'Unknown error'
}

export function useErrorHandler() {
  const callback = (error: ErrorType | AxiosError | any) => {
    console.error(error)

    if (!error) return

    const isAxiosError = error?.response !== undefined
    const duration = error?.lifeTime ?? ToastLifeTime.Long
    const backendMessage = extractBackendMessage(error)

    let description: ReactNode = <Text>{backendMessage}</Text>

    if (isAxiosError && error.response?.status) {
      switch (error.response.status) {
        case 400:
          description = (
            <Paragraph>
              <Text strong>{backendMessage}</Text>
            </Paragraph>
          )
          break

        case 404:
          description = (
            <Paragraph>
              <Text strong>Not found</Text>
              <br />
              <Text type="secondary">{backendMessage}</Text>
            </Paragraph>
          )
          break

        case 409:
          description = (
            <Paragraph>
              <Text strong>Conflict</Text>
              <br />
              <Text type="secondary">{backendMessage}</Text>
            </Paragraph>
          )
          break

        case 500:
          description = (
            <Paragraph>
              <Text strong>Server error</Text>
              <br />
              <Text type="secondary">{backendMessage}</Text>
            </Paragraph>
          )
          break

        default:
          description = (
            <Paragraph>
              <Text strong>{`Error ${error.response.status}`}</Text>
              <br />
              <Text type="secondary">{backendMessage}</Text>
            </Paragraph>
          )
      }
    }

    notification.error({
      title: 'Error',
      description,
      duration,
      placement: 'bottomRight',
      onClose: error?.onClose,
    })
  }

  return callback
}
